<template>
  <div>
    <!-- Class record: one subject's scores for one class, per quarter -->
    <v-dialog v-model="dialog" fullscreen persistent eager scrollable>
      <v-form ref="UserVerifyFormref" class="cr" @submit.prevent>
        <header class="cr-header">
          <div class="cr-header__text">
            <h2 class="cr-header__title">Class record</h2>
            <p v-if="data" class="cr-header__subtitle">{{ classLine }}</p>
          </div>
          <v-btn icon="mdi-close" variant="text" aria-label="Close" @click="closeD()" />
        </header>

        <div class="cr-body">
          <!-- Period filters and the record's tools -->
          <div class="cr-filters">
            <v-select
              v-if="data"
              v-model="quarter"
              :items="quarterOptions"
              label="Quarter"
              hide-details
              class="cr-filters__field"
              @update:model-value="loadData()"
            />
            <v-select
              v-model="semester"
              :label="semesterLabel"
              :disabled="data && !['Grade 11', 'Grade 12'].includes(data.grade_level)"
              :items="semesterOptions"
              hide-details
              class="cr-filters__field"
              @update:model-value="changeQuarter"
            />
            <v-select
              v-if="sub_subject != null"
              v-model="sub_subject"
              label="Sub-subject"
              :items="subSubjectList"
              item-value="id"
              item-title="description"
              :disabled="subSubjectList.length === 0"
              hide-details
              class="cr-filters__field"
              @update:model-value="changeQuarter"
            />
            <div class="cr-filters__end">
              <v-btn variant="outlined" prepend-icon="mdi-printer-outline" @click="data && data.subjectId == 6 ? openMAPEHQuiz() : openStudentsQuiz()">
                Print
              </v-btn>
              <v-btn
                v-if="edit"
                color="primary"
                prepend-icon="mdi-send-check-outline"
                :disabled="!gradeData.length || editingScores"
                @click="confirmSubmitGrade()"
              >
                Submit grades
              </v-btn>
            </div>
          </div>

          <div v-if="submitted" class="cr-banner">
            <v-icon size="18">mdi-lock-outline</v-icon>
            {{ quarter }} grades have been submitted. Scores for this quarter can no longer be changed.
          </div>

          <!-- Editing one assessment: what's being edited, a search to find
               a student, and Cancel / Save, in one bar. The tabs are hidden
               meanwhile so the component can't change with unsaved scores. -->
          <div v-if="editingScores" class="cr-editbar">
            <div class="cr-editbar__title">
              <v-icon size="18">mdi-pencil-outline</v-icon>
              <span>Editing <strong>{{ editingColumn?.title }}</strong></span>
              <span class="cr-editbar__max">out of {{ editingColumn?.max }}</span>
            </div>
            <!-- The table narrows to the matches; Enter jumps to the first
                 one's score box. -->
            <v-text-field
              id="cr-edit-search"
              v-model="tableSearch"
              prepend-inner-icon="mdi-magnify"
              placeholder="Find a student by name or LRN"
              density="compact"
              hide-details
              clearable
              autofocus
              class="cr-editbar__search"
              @keydown.enter.prevent="focusEditInput(0)"
            />
            <div class="cr-editbar__actions">
              <v-btn variant="outlined" :disabled="savingEdits" @click="cancelEditScores()">Cancel</v-btn>
              <v-btn color="primary" prepend-icon="mdi-content-save-outline" :loading="savingEdits" @click="saveScoreEdits()">
                Save changes
              </v-btn>
            </div>
          </div>

          <!-- Component tabs, Edit scores and Add score -->
          <div v-else class="cr-toolbar">
            <PageTabs :model-value="tab" :items="tabList" @change="changeTab" />
            <!-- Narrows the table to matching students; kept when editing
                 starts, so a student found here can be edited right away. -->
            <v-text-field
              id="cr-table-search"
              v-model="tableSearch"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search students by name or LRN"
              density="compact"
              hide-details
              clearable
              class="cr-toolbar__search"
            />
            <div v-if="edit" class="cr-toolbar__end">
              <!-- Pick the one assessment to edit -->
              <v-menu v-if="recordedColumns.length" location="bottom end">
                <template #activator="{ props: menuProps }">
                  <v-btn
                    v-bind="menuProps"
                    variant="outlined"
                    prepend-icon="mdi-pencil-outline"
                    append-icon="mdi-chevron-down"
                    :disabled="submitted"
                  >
                    Edit scores
                  </v-btn>
                </template>
                <v-list density="compact" min-width="200">
                  <v-list-subheader>Choose a {{ entryKind }} to edit</v-list-subheader>
                  <v-list-item
                    v-for="col in recordedColumns"
                    :key="col.key"
                    :title="col.title"
                    :subtitle="`Out of ${col.max}`"
                    @click="startEditScores(col.key)"
                  />
                </v-list>
              </v-menu>
              <v-btn color="primary" prepend-icon="mdi-plus" :disabled="submitted" @click="startAddScore()">
                Add score
              </v-btn>
            </div>
          </div>

          <div class="cr-table">
            <!-- Rebuilt whenever the columns change (a score saved, editing
                 started or finished): Vuetify otherwise reuses the header
                 cells, and a score column's tint stayed on whichever column
                 took its place, e.g. Initial grade after saving. -->
            <v-data-table
              :key="tableLayoutKey"
              :headers="tableHeaders"
              :items="tableRows"
              :items-per-page="-1"
              :group-by="[{ key: 'sex', order: 'desc' }]"
              fixed-header
              height="100%"
              density="compact"
              item-key="name"
              hide-default-footer
            >
              <template #group-header="{ item, columns, toggleGroup, isGroupOpen }">
                <tr class="cr-group" @vue:mounted="!isGroupOpen(item) && toggleGroup(item)">
                  <td :colspan="columns.length">
                    <button type="button" class="cr-group__btn" @click="toggleGroup(item)">
                      <v-icon size="16">{{ isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                      {{ item.value }}
                      <span class="cr-group__count">{{ item.items?.length }}</span>
                    </button>
                  </td>
                </tr>
              </template>

              <template #[`item.recommendation`]="{ item }">
                <div class="cr-reco">
                  <template v-if="editPassAdvice(item)?.kind === 'passed'">
                    <UiBadge variant="green">On track</UiBadge>
                    <span class="cr-advice">passes whatever the {{ editingColumn?.title }} score</span>
                  </template>
                  <template v-else-if="editPassAdvice(item)?.kind === 'needed'">
                    <UiBadge :variant="editPassAdvice(item).met ? 'green' : 'amber'">
                      Needs {{ editPassAdvice(item).needed }} / {{ editPassAdvice(item).highest }}
                    </UiBadge>
                    <span class="cr-advice">on {{ editingColumn?.title }} to reach 75</span>
                  </template>
                  <template v-else-if="editPassAdvice(item)?.kind === 'impossible'">
                    <UiBadge variant="red">Can't reach 75</UiBadge>
                    <span class="cr-advice">even with a perfect {{ editingColumn?.title }} score</span>
                  </template>
                  <span v-else class="cr-cell--empty">—</span>
                </div>
              </template>
              <template #[`item.initial_grade`]="{ item }">
                <span :class="{ 'cr-cell--empty': item.initial_grade == null }">{{ item.initial_grade ?? '—' }}</span>
              </template>
              <template #[`item.average_grade`]="{ item }">
                <span :class="{ 'cr-cell--empty': item.average_grade == null }">{{ item.average_grade ?? '—' }}</span>
              </template>
              <template #[`item.final_grade`]="{ item }">
                <strong v-if="item.final_grade != null">{{ item.final_grade }}</strong>
                <span v-else class="cr-cell--empty">—</span>
              </template>
              <template #[`item.grade_status`]="{ item }">
                <UiBadge v-if="item.final_grade != null" :variant="gradeStatus(item.final_grade).variant">
                  {{ gradeStatus(item.final_grade).text }}
                </UiBadge>
                <span v-else class="cr-cell--empty">—</span>
              </template>
              <template #[`item.data-table-group`]="{ item }">
                <span class="cr-cell--group">{{ item.sex }}</span>
              </template>
              <template v-for="col in recordedColumns" :key="col.key" #[`item.${col.key}`]="{ item }">
                <v-text-field
                  v-if="editingKey === col.key"
                  v-model.number="draftScores[item.id][col.key]"
                  type="number"
                  min="0"
                  :max="col.max"
                  density="compact"
                  hide-details
                  class="cr-cell-input"
                  :aria-label="`${col.title} score for ${item.name}`"
                  @update:model-value="clampDraft(item.id, col)"
                  @keydown.enter.prevent="focusNextEditInput($event)"
                />
                <span v-else :class="{ 'cr-cell--empty': item[col.key] == null }">{{ item[col.key] ?? '—' }}</span>
              </template>

              <template #no-data>
                <div class="cr-empty">
                  {{ tableSearch ? `No student matches “${tableSearch}”.` : 'No students in this class yet.' }}
                </div>
              </template>
            </v-data-table>
          </div>
        </div>

        <footer class="cr-footer">
          <v-btn variant="outlined" @click="closeD()">Close</v-btn>
        </footer>
      </v-form>
    </v-dialog>

    <!-- Add score: name it, set the total, find a student and type their
         score. Enter moves to the next student in the list. -->
    <UiDialog
      v-model="addScoreMode"
      :title="`Add ${entryKind}`"
      :description="[currentTabName, quarter, classLine].filter(Boolean).join(' · ')"
      size="lg"
      @close="cancelAddScore()"
    >
      <div class="cr-add">
        <div class="cr-add__fields">
          <v-text-field
            id="cr-add-title"
            v-model="title"
            label="Title"
            :maxlength="25"
            hide-details="auto"
            class="cr-add__title"
          />
          <v-text-field
            id="cr-add-total"
            ref="totalField"
            v-model="dinominator"
            label="Out of"
            type="number"
            min="1"
            placeholder="e.g. 20"
            hide-details="auto"
            class="cr-add__total"
            @keydown.enter.prevent="focusSearch()"
          />
        </div>

        <p v-if="!hasTotal" class="cr-add__hint">Enter the total first, then the scores.</p>
        <template v-else>
          <div class="cr-add__bar">
            <v-text-field
              id="cr-add-search"
              ref="searchField"
              v-model="studentSearch"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search a student by name or LRN"
              hide-details
              clearable
              class="cr-add__search"
              @keydown.enter.prevent="focusScore(0)"
            />
            <div class="cr-add__progress">
              <strong>{{ scoredCount }}</strong> of {{ studentList.length }} scored
              <span v-if="scoredCount < studentList.length"> · blanks save as 0</span>
            </div>
            <v-btn variant="text" size="small" @click="fillBlanksWithTotal()">Give blanks full marks</v-btn>
          </div>

          <!-- Periodic assessment: a middle column says what each student
               needs on this exam to pass the subject. -->
          <div class="cr-add__list" :class="{ 'cr-add__list--advice': showPassAdvice }">
            <div v-if="showPassAdvice" class="cr-add__cols">
              <span>Student</span>
              <span>Needed to pass</span>
              <span>Score</span>
            </div>
            <template v-for="group in scoreGroups" :key="group.label">
              <div class="cr-add__group">{{ group.label }} <span>{{ group.students.length }}</span></div>
              <div v-for="student in group.students" :key="student.studentListId || student.name" class="cr-add__row">
                <div class="cr-add__who">
                  <span class="cr-add__name">{{ student.name }}</span>
                  <span v-if="student.lrnNo" class="cr-add__lrn">LRN {{ student.lrnNo }}</span>
                </div>
                <div v-if="showPassAdvice" class="cr-add__need">
                  <template v-if="passAdvice(student)?.kind === 'passed'">
                    <UiBadge variant="green">On track</UiBadge>
                  </template>
                  <template v-else-if="passAdvice(student)?.kind === 'needed'">
                    <strong>{{ passAdvice(student).needed }} / {{ passAdvice(student).highest }}</strong>
                    <span class="cr-advice">at least, to reach 75</span>
                  </template>
                  <template v-else-if="passAdvice(student)?.kind === 'impossible'">
                    <UiBadge variant="red">Can't reach 75</UiBadge>
                    <span class="cr-advice">even with {{ passAdvice(student).highest }} / {{ passAdvice(student).highest }}</span>
                  </template>
                  <span v-else class="cr-cell--empty">—</span>
                </div>
                <div class="cr-score">
                  <v-text-field
                    v-model.number="student.quarterScore"
                    type="number"
                    :max="dinominator"
                    min="0"
                    hide-details
                    class="cr-score__input"
                    :aria-label="`Score for ${student.name}`"
                    @input="onInputGradeScore(student)"
                    @keydown.enter.prevent="focusNextScore($event)"
                  />
                  <span class="cr-score__max">/ {{ dinominator }}</span>
                </div>
              </div>
            </template>
            <p v-if="!scoreGroups.length" class="cr-add__hint">No student matches “{{ studentSearch }}”.</p>
          </div>
        </template>
      </div>

      <template #footer>
        <v-btn variant="outlined" @click="cancelAddScore()">Cancel</v-btn>
        <v-btn color="primary" :loading="savingScores" :disabled="!hasTotal || !title" @click="save()">
          Save {{ title || 'scores' }}
        </v-btn>
      </template>
    </UiDialog>


    <fade-away-message-component
      displayType="variation2"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :top="fadeAwayMessage.top"
      :type="fadeAwayMessage.type"
    ></fade-away-message-component>
  </div>
</template>

<script>
import eventBus from "@/eventBus";
import PageTabs from "@/components/Utils/PageTabs.vue";
import UiBadge from "@/components/ui/UiBadge.vue";
import UiDialog from "@/components/ui/UiDialog.vue";
import { useConfirm } from "@/composables/useConfirm";
import { useToast } from "@/composables/useToast";

export default {
  components: { PageTabs, UiBadge, UiDialog },
  setup() {
    const { confirm } = useConfirm();
    return { confirm, toast: useToast() };
  },
  props: {
    data: Object,
    action: String,
  },
  data() {
    return {
      readonly: true,
      dialog: false,
      // When true, the Highest Possible Score / Title fields and the
      // per-student Score column are shown (triggered by the Add Score button).
      addScoreMode: false,
      // Scores already recorded, shown as columns: [{ key, title, max }] and
      // { studentId: { key: score } }.
      recordedColumns: [],
      recordedScores: {},
      // Each recorded score's row id, { studentId: { key: id } }, and the
      // values being edited in the table. One assessment is edited at a
      // time: editingKey is its column.
      recordedIds: {},
      editingKey: null,
      draftScores: {},
      savingEdits: false,
      studentSearch: "",
      // The table's search box (shown in the toolbar and in the edit bar).
      tableSearch: "",
      syType: null,
      sub_subject: null,
      gradeData: [],
      newData: [],
      // The student whose scores are being edited in Score records. Declared
      // here so the row's Save button appears when it is set.
      savingScores: false,
      search: "",
      loading: false,
      title: null,
      quarter: "",
      semester: null,
      dinominator: null,
      message: null,
      status: null,
      type: null,
      filter: null,
      userRoleID: null,
      studentList: [],
      subSubjectList: [],
      edit: true,
      conflictData: null,
      decisionData: [],
      // Lowest initial grade the Transmuted Grade table turns into a 75.
      passingInitialGrade: null,
      activeTab: {
        id: 1,
        name: "For Verification",
        active: true,
      },
      tab: 1,
      tabList: [
        { id: 1, name: "Written Works", active: true },
        { id: 2, name: "Performance Tasks", active: false },
        { id: 3, name: "Periodic Assessment", active: false },
      ],
      headers: [
        { title: "Name", align: "start", sortable: false, key: "name" },
        {
          title: "Score",
          align: "center",
          sortable: false,
          key: "quarterScore",
          width: "200",
        },
      ],
      headers1: [
        { title: "Name", align: "start", sortable: false, key: "name" },
        {
          title: "Recommendation",
          align: "center",
          sortable: false,
          key: "records",
        },
        {
          title: "Score",
          align: "center",
          sortable: false,
          key: "quarterScore",
          width: "200",
        },
      ],
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
      subject: null,
    };
  },
  computed: {
    // The class with one column per score already recorded for this
    // component and period (e.g. Quiz 1, Quiz 2); new scores are typed in
    // the Add score dialog.
    tableHeaders() {
      const base = this.tab != 3 ? this.headers : this.headers1;
      return [
        // Rows are grouped Male / Female; Vuetify adds this column for the
        // grouping and it takes its title and width from here.
        { title: "Group", key: "data-table-group", sortable: false, width: 110, minWidth: 110 },
        ...base.filter((h) => !["quarterScore", "records"].includes(h.key)),
        // Editing a periodic assessment: how each student can still pass.
        ...(this.showEditAdvice
          ? [{ title: "Recommendation", key: "recommendation", sortable: false, width: 230, minWidth: 230, cellProps: { class: "cr-col-reco" }, headerProps: { class: "cr-col-reco" } }]
          : []),
        // Scores sit together, one fixed-width column each, boxed in by
        // borders (the last one also closes the box on its right).
        ...this.recordedColumns.map((c, i, all) => ({
          title: `${c.title} (${c.max})`,
          key: c.key,
          align: "center",
          sortable: false,
          width: 120,
          minWidth: 120,
          // The column being edited is highlighted.
          cellProps: { class: this.scoreColumnClass(c, i === all.length - 1) },
          headerProps: { class: this.scoreColumnClass(c, i === all.length - 1) },
        })),
        // The period's grade from those scores (what Submit grades sends).
        { title: "Initial grade", key: "initial_grade", align: "center", sortable: false, width: 120, minWidth: 120, cellProps: { class: "cr-col-grade" }, headerProps: { class: "cr-col-grade" } },
        // A term-based year averages the initial grades of every term graded
        // so far, and the term grade is transmuted from that average.
        ...(this.isTermPeriod
          ? [{ title: "Average grade", key: "average_grade", align: "center", sortable: false, width: 120, minWidth: 120, cellProps: { class: "cr-col-grade" }, headerProps: { class: "cr-col-grade" } }]
          : []),
        { title: this.isTermPeriod ? "Term grade" : "Final grade", key: "final_grade", align: "center", sortable: false, width: 110, minWidth: 110, cellProps: { class: "cr-col-grade" }, headerProps: { class: "cr-col-grade" } },
        { title: "Status", key: "grade_status", align: "center", sortable: false, width: 110, minWidth: 110, cellProps: { class: "cr-col-grade" }, headerProps: { class: "cr-col-grade" } },
      ];
    },
    // The columns and which one is being edited; changes rebuild the table.
    tableLayoutKey() {
      return [...this.tableHeaders.map((h) => h.key), this.editingKey ?? ""].join("|");
    },
    // Each student's grade row. A subject with components (e.g. MAPEH) gets
    // one row per component plus one for the whole subject: show the
    // selected component's, matching the scores on screen.
    gradesByStudent() {
      const component = this.sub_subject != null
        ? this.subSubjectList.find((c) => String(c.id) === String(this.sub_subject))?.description
        : null;
      const wanted = component || this.data?.subject_title;
      const byStudent = {};
      for (const row of this.gradeData) {
        const id = String(row.studentID);
        if (!byStudent[id] || row.subject_title === wanted) byStudent[id] = row;
      }
      return byStudent;
    },
    tableRows() {
      // The search box narrows the class to the matches (in both modes).
      const q = (this.tableSearch || "").trim().toLowerCase();
      const students = q
        ? this.studentList.filter((s) => String(s.name).toLowerCase().includes(q) || String(s.lrnNo ?? "").includes(q))
        : this.studentList;
      return students.map((s) => {
        const grade = this.gradesByStudent[String(s.id)];
        return {
          ...s,
          ...(this.recordedScores[s.id] || {}),
          initial_grade: grade ? grade.initial_grade : null,
          average_grade: grade ? grade.average_grade ?? null : null,
          final_grade: grade ? grade.transmuted_grade : null,
        };
      });
    },
    entryKind() {
      return this.tab == 1 ? "quiz" : this.tab == 2 ? "performance task" : "exam";
    },
    // The Add score list: Male then Female, narrowed by the search box.
    scoreGroups() {
      const q = (this.studentSearch || "").trim().toLowerCase();
      const match = (s) => !q || String(s.name).toLowerCase().includes(q) || String(s.lrnNo ?? "").includes(q);
      return ["Male", "Female"]
        .map((sex) => ({
          label: sex,
          students: this.studentList.filter((s) => String(s.sex).toLowerCase() === sex.toLowerCase() && match(s)),
        }))
        .concat([{ label: "Other", students: this.studentList.filter((s) => !["male", "female"].includes(String(s.sex).toLowerCase()) && match(s)) }])
        .filter((g) => g.students.length);
    },
    currentTabName() {
      const current = this.tabList.find((t) => t.id === this.tab);
      return current ? current.name : "";
    },
    classLine() {
      if (!this.data) return "";
      return [this.data.subject_title, this.data.grade_level, this.data.room_section]
        .filter(Boolean)
        .join(" · ");
    },
    // A term of a 3-term year (Senior High still uses quarters there).
    isTermPeriod() {
      return this.syType == 1 && /term$/i.test(String(this.quarter || "").trim());
    },
    quarterOptions() {
      if (!this.data) return [];
      const g = this.data.grade_level;
      if (g == "Grade 11" || (g == "Grade 12" && this.syType == 0)) return ["1st Quarter", "2nd Quarter"];
      return this.syType == 0
        ? ["1st Quarter", "2nd Quarter", "3rd Quarter", "4th Quarter"]
        : ["1st Term", "2nd Term", "3rd Term"];
    },
    semesterOptions() {
      return this.syType == 0 ? ["1st Semester", "2nd Semester"] : this.syType == 1 ? ["Senior High"] : ["Junior High"];
    },
    semesterLabel() {
      return this.data && !["Grade 11", "Grade 12"].includes(this.data.grade_level)
        ? "Grade level"
        : this.syType == 1
        ? "Grade level"
        : "Semester";
    },
    // Classes for a score column: the edited one is highlighted, the last one
    // gets the box's right border.
    scoreColumnClass() {
      return (col, last) => ["cr-col-score", { "cr-col-score--editing": col.key === this.editingKey, "cr-col-score--last": last }];
    },
    // The Recommendation column: while editing a periodic assessment.
    showEditAdvice() {
      return this.tab == 3 && this.editingScores;
    },
    // The Needed to pass column: only when adding a periodic assessment.
    showPassAdvice() {
      return this.tab == 3;
    },
    editingScores() {
      return this.editingKey != null;
    },
    editingColumn() {
      return this.recordedColumns.find((c) => c.key === this.editingKey) ?? null;
    },
    hasTotal() {
      return Number(this.dinominator) > 0;
    },
    scoredCount() {
      return this.studentList.filter(
        (s) => s.quarterScore !== null && s.quarterScore !== undefined && s.quarterScore !== "",
      ).length;
    },
    // The quarter's grades were already submitted to parents.
    submitted() {
      return Number(this.conflictData) > 0;
    },
  },
  watch: {
    data: {
      handler(data) {
        if (!data) return;
        this.dialog = true;
        this.filter = this.$store.getters.getFilterSelected;
        this.syType = this.$store.getters.getSyType;
        // if (data.id) {
        //   data.grade_level == 'Grade 11' ||
        //   (data.grade_level == 'Grade 12' && this.syType == 0)
        //     ? (this.semester = '1st Semester')
        //     : data.grade_level == 'Grade 11' ||
        //       (data.grade_level == 'Grade 12' && this.syType == 1)
        //     ? (this.semester = 'Senior High')
        //     : (this.semester = 'Junior High');
        //   this.subSubjectList =
        //     data.sub_subject != null && this.syType == 0
        //       ? JSON.parse(data.sub_subject)
        //       : JSON.parse(data.sub_subject_term);

        //   this.sub_subject =
        //     data.sub_subject != null ? this.subSubjectList[0].id : null;
        // }
        if (data.id) {
          data.grade_level == "Grade 11" ||
          (data.grade_level == "Grade 12" && this.syType == 0)
            ? (this.semester = "1st Semester")
            : data.grade_level == "Grade 11" ||
              (data.grade_level == "Grade 12" && this.syType == 1)
            ? (this.semester = "Senior High")
            : (this.semester = "Junior High");

          // Determine which sub-subject data to use
          const subSubjectData =
            data.sub_subject != null && this.syType == 0
              ? data.sub_subject
              : data.sub_subject_term;

          // Always initialize as an array
          this.subSubjectList = [];

          if (subSubjectData) {
            try {
              const parsed = JSON.parse(subSubjectData);

              // Make sure the parsed value is actually an array
              if (Array.isArray(parsed)) {
                this.subSubjectList = parsed;
              }
            } catch {
              this.subSubjectList = [];
            }
          }

          // Only access [0] if an item actually exists
          if (this.subSubjectList.length > 0) {
            this.sub_subject = this.subSubjectList[0]?.id ?? null;
          } else {
            this.sub_subject = null;
          }
        }
        // Load only now: the scores are looked up by semester and
        // sub-subject, which weren't set yet when this ran first.
        this.initialize();
        this.checkConflict();
      },
      deep: true,
    },
  },

  methods: {
    loadData() {
      this.reset();
      this.changeQuarter();
    },

    initialize() {
      this.filter = this.$store.getters.getFilterSelected;
      this.syType = this.$store.getters.getSyType;
      // The term chosen on Class records when this class has it, otherwise
      // the first period the Quarter picker offers (Junior High in a
      // quarter-based year used to start on "1st Term", which has no scores).
      this.quarter = this.quarterOptions.includes(this.data?.term) ? this.data.term : this.quarterOptions[0];
      this.editingKey = null;
      this.tableSearch = "";
      this.userRoleID = this.$store.state.user.id;
      this.getTaggedStudent();
    },
    // Reveal the Highest Possible Score / Title fields and Score column.
    // Opens the entry with the next title filled in (e.g. "Quiz 3") and the
    // cursor in "Out of", so a teacher only types the total and the scores.
    async startAddScore() {
      this.addScoreMode = true;
      this.studentSearch = "";
      this.dinominator = null;
      this.title = `${this.entryPrefix()} 1`;
      this.$nextTick(() => this.$refs.totalField?.focus());
      try {
        const res = await this.axiosCall(
          `/rooms-section/getAllGradeByQuarter/${this.quarter}/${this.semester}/${this.data.roomId}/${this.data.subjectId}/${this.tab}/${this.filter}/${this.sub_subject}`,
          "GET",
        );
        const taken = new Set((Array.isArray(res?.data) ? res.data : []).map((r) => r.quiz_label)).size;
        this.title = `${this.entryPrefix()} ${taken + 1}`;
      } catch {
        // Keep "… 1"; the teacher can rename it.
      }
    },
    entryPrefix() {
      return this.tab == 1 ? "Quiz" : this.tab == 2 ? "Task" : "Exam";
    },
    // Enter in a score box: after a search, clears it and goes back to the
    // search box for the next name; otherwise moves to the next student's
    // box, in table order. The score is already kept on the student.
    focusNextScore(event) {
      if ((this.studentSearch || "").trim()) {
        this.studentSearch = "";
        this.focusSearch();
        return;
      }
      const inputs = [...this.$el.ownerDocument.querySelectorAll(".cr-score__input input")];
      const next = inputs[inputs.indexOf(event.target) + 1];
      if (next) {
        next.focus();
        next.select?.();
      } else {
        event.target.blur();
      }
    },
    focusScore(index) {
      this.$nextTick(() => {
        const input = this.$el.ownerDocument.querySelectorAll(".cr-score__input input")[index];
        input?.focus();
        input?.select?.();
      });
    },
    // After the total: to the search box, so the teacher can find a student.
    focusSearch() {
      if (!this.hasTotal) return;
      this.$nextTick(() => this.$el.ownerDocument.getElementById("cr-add-search")?.focus());
    },
    fillBlanksWithTotal() {
      this.studentList.forEach((s) => {
        if (s.quarterScore === null || s.quarterScore === undefined || s.quarterScore === "") {
          s.quarterScore = Number(this.dinominator);
        }
      });
    },
    // Hide the entry fields and discard anything typed so far.
    cancelAddScore() {
      this.addScoreMode = false;
      this.dinominator = null;
      this.title = null;
      this.getTaggedStudent();
    },
    onInputGradeScore(item) {
      if (item.quarterScore > this.dinominator) {
        item.quarterScore = this.dinominator;
      } else if (item.quarterScore < 0) {
        item.quarterScore = 0;
      }
    },
    // The period's grades for the class (the table's grade columns and what
    // Submit grades sends), loaded with the recorded scores.
    loadGrades() {
      if (!this.data || !this.quarter) {
        this.gradeData = [];
        return;
      }
      let subjectID = this.sub_subject == null ? "noData" : this.sub_subject;
      this.axiosCall(
        "/rooms-section/getGeneratedGrade/" +
          this.data.roomId +
          "/" +
          this.filter +
          "/" +
          this.quarter +
          "/" +
          this.semester +
          "/" +
          this.data.subjectId +
          "/" +
          subjectID,
        "GET",
      ).then((res) => {
        if (res.data) {
          this.gradeData = res.data.grades;
          this.passingInitialGrade = res.data.passing_initial_grade ?? null;
        }
      });
    },
    // Now matches the >=80 / >=75 / below-75 thresholds used everywhere
    // else in this file (the status chip text, the SF9 logic). Previously
    // `grade < 80` was checked first, so `grade < 75` never fired and
    // every failing grade showed as 'warning' instead of 'error'.
    getColor(grade) {
      if (grade < 75) {
        this.status = "At-Risk";
        return "error";
      } else if (grade < 80) {
        this.status = "Warning";
        return "warning";
      } else {
        this.status = "Passed";
        return "success";
      }
    },
    changeTab(tabItem) {
      this.activeTab = tabItem;
      this.tab = tabItem.id;
      this.changeQuarter();
      this.tabList.forEach((t) => {
        t.active = t.id === tabItem.id;
      });
      if (tabItem.id == 3) {
        this.decisionSupport();
      }
    },

    // Same order as the printed class record: each sex A–Z by "SURNAME,
    // First M.". Sex is tidied so stray casing or spaces don't open a
    // separate group; the table's group-by (desc) then puts Male first.
    sortedClassList(students) {
      const tidySex = (sex) => {
        const v = String(sex ?? "").trim().toLowerCase();
        return v === "male" ? "Male" : v === "female" ? "Female" : String(sex ?? "").trim();
      };
      return students
        .map((s) => ({ ...s, sex: tidySex(s.sex) }))
        .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, { sensitivity: "base" }));
    },

    getTaggedStudent() {
      this.axiosCall(
        "/rooms-section/getMyStudentClassRecords/" +
          this.userRoleID +
          "/" +
          this.filter +
          "/" +
          this.data.roomId,
        "GET",
      ).then((res) => {
        if (res.data) {
          this.studentList = this.sortedClassList(res.data);
        }
      });
      this.loadRecordedScores();
    },
    // The scores recorded for the current component, period and sub-subject,
    // the same data as Score records, turned into one column per assessment.
    async loadRecordedScores() {
      if (!this.data || !this.quarter) {
        this.recordedColumns = [];
        this.recordedScores = {};
        return;
      }
      try {
        const res = await this.axiosCall(
          `/rooms-section/getAllGradeByQuarter/${this.quarter}/${this.semester}/${this.data.roomId}/${this.data.subjectId}/${this.tab}/${this.filter}/${this.sub_subject}`,
          "GET",
        );
        const rows = Array.isArray(res?.data) ? res.data : [];
        const number = (label) => Number(String(label).replace(/\D/g, "")) || 0;
        const labels = [...new Set(rows.map((r) => r.quiz_label))].sort((a, b) => number(a) - number(b));
        this.recordedColumns = labels.map((label) => {
          const first = rows.find((r) => r.quiz_label === label);
          return {
            key: `score_${label.replace(/\s+/g, "")}`,
            title: first?.SG_title?.trim() || label,
            max: first?.SG_highest_posible_score,
          };
        });
        const scores = {};
        const ids = {};
        rows.forEach((r) => {
          const key = `score_${r.quiz_label.replace(/\s+/g, "")}`;
          (scores[r.SG_studentID] ||= {})[key] = r.SG_quarterScore;
          (ids[r.SG_studentID] ||= {})[key] = r.SG_id;
        });
        this.recordedScores = scores;
        this.recordedIds = ids;
        this.loadGrades();
      } catch {
        this.recordedColumns = [];
        this.recordedScores = {};
        this.recordedIds = {};
      }
    },
    // Edit one recorded assessment in place: its cells become boxes.
    startEditScores(key) {
      const drafts = {};
      this.studentList.forEach((s) => {
        drafts[s.id] = { ...(this.recordedScores[s.id] || {}) };
      });
      this.draftScores = drafts;
      this.editingKey = key;
    },
    cancelEditScores() {
      this.editingKey = null;
      this.draftScores = {};
    },
    // Score boxes of the assessment being edited, in table order.
    editInputs() {
      return [...this.$el.ownerDocument.querySelectorAll(".cr-cell-input input")];
    },
    focusEditInput(index) {
      this.$nextTick(() => {
        const input = this.editInputs()[index];
        input?.focus();
        input?.select?.();
      });
    },
    // Enter in a score box moves to the next student's box.
    focusNextEditInput(event) {
      const inputs = this.editInputs();
      const next = inputs[inputs.indexOf(event.target) + 1];
      if (next) {
        next.focus();
        next.select?.();
      } else {
        event.target.blur();
      }
    },
    clampDraft(studentId, col) {
      const row = this.draftScores[studentId];
      const value = row[col.key];
      if (value === "" || value == null) return;
      if (value > Number(col.max)) row[col.key] = Number(col.max);
      else if (value < 0) row[col.key] = 0;
    },
    // Saves only the scores that changed, one request per student (the
    // same endpoint as Score records). A cleared box saves as 0. Students
    // with no score for this assessment yet (e.g. they joined the class
    // after it was given) get one only if a score was typed for them.
    async saveScoreEdits() {
      const requests = [];
      let saved = 0;
      const col = this.editingColumn;
      const templateId = col && Object.values(this.recordedIds).map((ids) => ids[col.key]).find(Boolean);
      const missing = col
        ? Object.entries(this.draftScores)
            .filter(([studentId, draft]) => !this.recordedIds[studentId]?.[col.key] && draft[col.key] !== "" && draft[col.key] != null)
            .map(([studentId, draft]) => ({ studentId: Number(studentId), value: Math.round(Number(draft[col.key])) }))
        : [];
      if (templateId && missing.length) {
        saved += missing.length;
        requests.push(
          this.axiosCall("/rooms-section/addMissingScores", "POST", { templateId: Number(templateId), scores: missing }),
        );
      }
      Object.entries(this.draftScores).forEach(([studentId, draft]) => {
        const changed = {};
        this.recordedColumns.filter((col) => col.key === this.editingKey).forEach((col) => {
          const id = this.recordedIds[studentId]?.[col.key];
          if (!id) return;
          const value = draft[col.key] === "" || draft[col.key] == null ? 0 : Math.round(Number(draft[col.key]));
          if (value === Number(this.recordedScores[studentId]?.[col.key])) return;
          // updateStudentGrade reads entries keyed "Quiz…" ({ id, value }).
          changed[col.key.replace(/^score_/, "")] = { id, value };
        });
        if (!Object.keys(changed).length) return;
        saved += 1;
        requests.push(
          this.axiosCall(`/rooms-section/updateStudentGrade/${studentId}`, "PATCH", {
            data: JSON.stringify(changed),
            quarter: this.quarter,
            semester: this.semester,
          }),
        );
      });
      if (!requests.length) {
        this.cancelEditScores();
        return;
      }
      this.savingEdits = true;
      try {
        const results = await Promise.all(requests);
        const failed = results.filter((res) => res?.data?.status != 200);
        if (failed.length) {
          this.toast.error(failed[0]?.data?.msg || "Some scores could not be saved.");
        } else {
          this.toast.success(`Scores saved for ${saved} ${saved === 1 ? "student" : "students"}.`);
          this.cancelEditScores();
        }
      } catch {
        this.toast.error("Could not save the scores.");
      } finally {
        this.savingEdits = false;
        await this.loadRecordedScores();
        if (this.tab == 3) this.decisionSupport();
      }
    },
    checkConflict() {
      this.axiosCall(
        "/rooms-section/getConflictQuarterGrade/" +
          this.semester +
          "/" +
          this.filter +
          "/" +
          this.quarter +
          "/" +
          this.data.roomId +
          "/" +
          this.data.subjectId,
        "GET",
      ).then((res) => {
        if (res.data) {
          this.conflictData = res.data.count_gen;

          if (res.data.count_gen == 0) {
            this.edit = true;
          } else {
            // The banner in the dialog says the quarter is submitted.
            this.edit = false;
          }
        }
      });
    },

    reset() {
      this.activeTab = { id: 1, name: "Written Works", active: true };
      this.tab = 1;
      this.dinominator = null;
      this.title = null;
      this.edit = false;
      this.addScoreMode = false;
    },

    changeQuarter() {
      this.cancelEditScores();
      this.loadRecordedScores();
      this.axiosCall(
        "/rooms-section/getConflictQuarterGrade/" +
          this.semester +
          "/" +
          this.filter +
          "/" +
          this.quarter +
          "/" +
          this.data.roomId +
          "/" +
          this.data.subjectId,
        "GET",
      ).then((res) => {
        if (res.data) {
          this.conflictData = res.data.count_gen;

          if (res.data.count_gen == 0) {
            this.edit = true;
          } else {
            this.edit = false;
            // Quarter is already submitted: leave entry mode if it was open.
            this.addScoreMode = false;
          }
        }
      });
    },
    checkAllGrades() {
      return this.studentList.every(
        (student) =>
          student.quarterScore !== null &&
          student.quarterScore !== undefined &&
          student.quarterScore !== "",
      );
    },
    editItem() {
    },

    save() {
      if (!this.title || !this.dinominator) {
        this.toast.error("Enter a title and the highest possible score first.");
      } else if (!this.checkAllGrades()) {
        // this.type = 'error';
        // this.message =
        //   'Some student have no score given please check before saving!';
        // this.showAlert = true;
        // setTimeout(() => (this.showAlert = false), 5000);
        this.studentList.forEach((student) => {
          if (
            student.quarterScore === null ||
            student.quarterScore === undefined ||
            student.quarterScore === ""
          ) {
            student.quarterScore = 0;
          }
        });

        this.studentList.every(
          (student) =>
            student.quarterScore !== null && student.quarterScore !== undefined,
        );

        this.confirmSave();
      } else {
        this.confirmSave();
      }
    },
    async askToConfirm(kind) {
      const saving = kind === "save";
      const ok = await this.confirm({
        title: saving ? `Save "${this.title}" scores?` : `Submit ${this.quarter} grades?`,
        description: saving
          ? `Scores out of ${this.dinominator} for ${this.currentTabName}. Students without a score get 0.`
          : "Parents will see these grades, and scores for this quarter can no longer be changed.",
        confirmText: saving ? "Save scores" : "Submit grades",
      });
      if (!ok) return;
      if (saving) this.confirmSave();
      else this.confirmSubmitGradeToParents();
    },
    gradeStatus(grade) {
      const g = Number(grade);
      if (g >= 80) return { text: "Passed", variant: "green" };
      if (g >= 75) return { text: "Warning", variant: "amber" };
      return { text: "At risk", variant: "red" };
    },
    confirmSubmitGrade() {
      if (this.sub_subject != null) {
        this.newData = this.getTransformData(this.gradeData);
      }

      const missing = this.missingPeriodicAssessment();
      if (this.gradeData.length <= 0 && this.newData.length <= 0) {
        this.fadeAwayMessage.show = true;
        this.fadeAwayMessage.type = "error";
        this.fadeAwayMessage.header = "System Message";
        this.fadeAwayMessage.message = "There is nothing to submit yet.";
      } else if (missing) {
        this.fadeAwayMessage.show = true;
        this.fadeAwayMessage.type = "error";
        this.fadeAwayMessage.header = "System Message";
        this.fadeAwayMessage.message = missing;
      } else {
        this.askToConfirm("submit");
      }
    },
    // Grades can't be submitted without the periodic assessment: its share
    // of the grade would count as 0, and the quarter locks once submitted.
    // A subject with components (e.g. MAPEH) needs one in each. The server
    // checks too.
    missingPeriodicAssessment() {
      const recorded = (rows) => rows.some((r) => Number(r.qa_highest) > 0);
      if (this.sub_subject == null) {
        return recorded(this.gradeData) ? "" : `Add the periodic assessment before submitting ${this.quarter} grades.`;
      }
      const missing = this.subSubjectList
        .map((c) => c.description)
        .filter((name) => !recorded(this.gradeData.filter((r) => r.subject_title === name)));
      return missing.length
        ? `Add the periodic assessment for ${missing.join(", ")} before submitting ${this.quarter} grades.`
        : "";
    },

    getTransformData(data) {
      const transformed = Object.values(
        data.reduce((acc, curr) => {
          const {
            studentID,
            name,
            subject_title,
            studentLRN,
            initial_grade,
            transmuted_grade,
          } = curr;

          if (!acc[studentID]) {
            acc[studentID] = {
              id: studentID,
              studentID: studentID,
              studentLRN,
              name,
              MAPEH: {
                initial_grade: null,
                transmuted_grade: null,
                sub_subjects: {},
              },
            };
          }

          // For a subject with components (e.g. MAPEH) the server sends one
          // row per component plus one titled after the subject: its total.
          // (The MAPEH key is just where the grades are kept for submitting.)
          if (subject_title === this.data.subject_title) {
            acc[studentID].MAPEH.initial_grade = initial_grade;
            acc[studentID].MAPEH.transmuted_grade = transmuted_grade;
          } else {
            acc[studentID].MAPEH.sub_subjects[subject_title] = {
              initial_grade,
              transmuted_grade,
            };
          }
          return acc;
        }, {}),
      );

      return transformed;
    },
    confirmSubmitGradeToParents() {
      let userId = this.$store.state.user.id;
      let data = {
        data:
          this.sub_subject == null
            ? JSON.stringify(this.gradeData)
            : JSON.stringify(this.newData),
        quarter: this.quarter,
        subjectID: this.data.subjectId,
        semester: this.semester,
        roomID: this.data.roomId,
        school_yearID: this.filter,
        teacherID: userId,
        sub_subject: this.sub_subject,
      };
      this.axiosCall("/rooms-section/quarterFinalGrade", "POST", data).then(
        (res) => {
          if (res.data.status == 201) {
            // The class record shows the quarter as submitted, with its
            // grades reloaded.
            this.changeQuarter();
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          } else if (res.data.status == 400) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
        },
      );
    },
    confirmSave() {
      let data = {
        data: JSON.stringify(this.studentList),
        quarter: this.quarter,
        subjectID: this.data.subjectId,
        sub_subject: this.sub_subject,
        semester: this.semester,
        highest_posible_score: this.dinominator,
        type: this.tab,
        title: this.title,
      };
      this.savingScores = true;
      this.axiosCall("/rooms-section/studentGrade", "POST", data).finally(() => {
        this.savingScores = false;
      }).then(
        (res) => {
          if (res.data.status == 201) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
                  // Stay in the class record: clear the entry and reload the
            // students; only Close or the × closes the dialog.
            this.getTaggedStudent();
            this.dinominator = null;
            this.title = null;
            this.addScoreMode = false;
            if (this.tab == 3) this.decisionSupport();
          } else if (res.data.status == 400) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
        },
      );
    },
    closeD() {
      eventBus.emit("closeStudentClassRecordDialog", true);

      this.studentList = [];
      this.quarter = "1st Quarter";
      this.dialog = false;
      this.dinominator = null;
      this.title = null;
      this.addScoreMode = false;
      this.cancelEditScores();

      this.tab = 1;
      this.activeTab = {
        id: 1,
        name: "For Verification",
        active: true,
      };

      this.tabList = this.tabList.map((tabItem) => ({
        ...tabItem,
        active: tabItem.id === 1,
      }));
    },
    decisionSupport() {
      let subjectID = this.sub_subject == null ? "noData" : this.sub_subject;
      this.axiosCall(
        "/rooms-section/getGeneratedGrade/" +
          this.data.roomId +
          "/" +
          this.filter +
          "/" +
          this.quarter +
          "/" +
          this.semester +
          "/" +
          this.data.subjectId +
          "/" +
          subjectID,
        "GET",
      ).then((res) => {
        if (res.data) {
          this.decisionData = res.data.grades;
          this.passingInitialGrade = res.data.passing_initial_grade ?? null;
          this.subject = res.data.subject;
        }
      });
    },
    // The initial grade this period needs for the student to pass: the one
    // that transmutes to 75, or in a term-based year, the one that lifts the
    // average of the terms graded so far to it. Null when it isn't known.
    termTarget(grade) {
      if (this.passingInitialGrade == null) return null;
      const passing = Number(this.passingInitialGrade);
      if (!Number.isFinite(passing)) return null;
      const n = Number(grade?.terms_counted);
      const average = Number(grade?.average_grade);
      const initial = Number(grade?.initial_grade);
      if (!this.isTermPeriod || !(n > 1) || !Number.isFinite(average) || !Number.isFinite(initial)) return passing;
      // The other terms' total stays as it is; only this term can change.
      return passing * n - (average * n - initial);
    },
    // What a student needs on the periodic assessment being edited to pass
    // the subject, from their saved grade: the component's score is the
    // total of its assessments over their total (DepEd), so the others stay
    // as saved and only the edited one is solved for. `met` says whether
    // the score typed in the box already reaches it.
    editPassAdvice(item) {
      const col = this.editingColumn;
      const grade = this.gradesByStudent[String(item.id)];
      const initial = Number(grade?.initial_grade);
      let weight = Number(this.data?.quarter_assessment);
      if (weight > 1) weight = weight / 100;
      const saved = this.recordedScores[item.id] || {};
      const has = (c) => this.recordedIds[item.id]?.[c.key];
      const target = this.termTarget(grade);
      if (!col || !has(col) || !Number.isFinite(initial) || !(weight > 0) || target == null) return null;

      const items = this.recordedColumns.filter(has);
      const totalMax = items.reduce((n, c) => n + Number(c.max), 0);
      const totalScore = items.reduce((n, c) => n + Number(saved[c.key] || 0), 0);
      if (!(totalMax > 0)) return null;
      // The rest of the grade (written works, performance tasks).
      const others = initial - (totalScore / totalMax) * weight * 100;
      const neededTotal = ((target - others) * totalMax) / (weight * 100);
      const needed = Math.ceil(neededTotal - (totalScore - Number(saved[col.key] || 0)) - 1e-9);
      const highest = Number(col.max);
      if (needed <= 0) return { kind: "passed", highest };
      if (needed > highest) return { kind: "impossible", highest };
      const typed = Number(this.draftScores[item.id]?.[col.key]);
      return { kind: "needed", needed, highest, met: Number.isFinite(typed) && typed >= needed };
    },
    // What a student needs on the periodic assessment being added to pass
    // the subject: { kind: 'passed' | 'needed' | 'impossible', needed,
    // highest }, or null when it can't be worked out (no grade yet, no
    // total typed). Uses the subject's periodic assessment weight.
    passAdvice(rec) {
      const record = this.decisionData.find(
        (d) => String(d.studentID) === String(rec.id),
      );

      if (!record) {
        return null;
      }

      const initialGrade = Number(record.initial_grade);
      const highest = Number(this.dinominator);

      let weight = Number(this.data.quarter_assessment ?? this.data.writen_works);

      // Handle either 30 or 0.30
      if (weight > 1) {
        weight = weight / 100;
      }

      if (
        !Number.isFinite(initialGrade) ||
        !Number.isFinite(highest) ||
        !Number.isFinite(weight) ||
        highest <= 0 ||
        weight <= 0
      ) {
        return null;
      }

      // Target initial grade: the lowest that transmutes to 75.
      const targetGrade = this.termTarget(record);
      if (targetGrade == null) return null;

      const remaining = targetGrade - initialGrade;

      if (remaining <= 0) {
        return { kind: "passed", highest };
      }

      // Required raw score
      const requiredScore = (remaining / weight) * (highest / 100);

      // Do not display a score higher than the maximum possible score
      const scoreNeeded = Math.ceil(requiredScore);

      if (scoreNeeded > highest) {
        return { kind: "impossible", highest };
      }

      return { kind: "needed", needed: scoreNeeded, highest };
    },

    openStudentsQuiz() {
      window.open(
        process.env.VUE_APP_SERVER +
          "/pdf-generator/getStudentSQuizes/" +
          this.quarter +
          "/" +
          this.semester +
          "/" +
          this.data.roomId +
          "/" +
          this.data.subjectId +
          "/" +
          this.filter +
          "/" +
          this.sub_subject +
          "/" +
          this.userRoleID +
          "",
        "_blank",
      );
    },
    openMAPEHQuiz() {
      window.open(
        process.env.VUE_APP_SERVER +
          "/pdf-generator/getMAPEHQuizes/" +
          this.quarter +
          "/" +
          this.semester +
          "/" +
          this.data.roomId +
          "/" +
          this.data.subjectId +
          "/" +
          this.filter +
          "/" +
          this.sub_subject +
          "/" +
          this.userRoleID +
          "",
        "_blank",
      );
    },
  },
};
</script>

<style scoped>
.cr {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--background);
}
.cr-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  background: var(--card);
  border-bottom: 1px solid var(--border);
}
.cr-header__text {
  flex: 1;
  min-width: 0;
}
.cr-header__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}
.cr-header__subtitle {
  margin: 2px 0 0;
  font-size: 14px;
  color: var(--muted-foreground);
}
.cr-body {
  /* Filters and tabs stay put; only the table below scrolls. */
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding: 20px 24px;
}
.cr-filters,
.cr-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.cr-filters__field {
  flex: 0 0 200px;
}
.cr-filters__end,
.cr-toolbar__end {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.cr-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--subtle);
}
.cr-body > :not(.cr-table) {
  flex-shrink: 0;
}
.cr-table {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--card);
}
/* The table scrolls in both directions inside its card, its header row
   stays visible, and the name column stays put as score columns scroll. */
.cr-table :deep(.v-table) {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}
.cr-table :deep(.v-table__wrapper) {
  flex: 1;
  min-height: 0;
  overflow: auto;
}
/* Group and Name stay on the left while the score columns scroll. Name
   takes the free width; Group is a fixed 110px. */
.cr-table :deep(thead th:nth-child(1)),
.cr-table :deep(tbody tr:not(.cr-group) td:nth-child(1)),
.cr-table :deep(thead th:nth-child(2)),
.cr-table :deep(tbody tr:not(.cr-group) td:nth-child(2)) {
  position: sticky;
  z-index: 1;
  background: var(--card);
}
.cr-table :deep(thead th:nth-child(1)),
.cr-table :deep(tbody tr:not(.cr-group) td:nth-child(1)) {
  left: 0;
  width: 110px;
  min-width: 110px;
  max-width: 110px;
}
.cr-table :deep(thead th:nth-child(2)),
.cr-table :deep(tbody tr:not(.cr-group) td:nth-child(2)) {
  left: 110px;
  width: 100%;
  min-width: 240px;
  box-shadow: inset -1px 0 0 var(--border);
}
/* Header row: one shaded band with small caps labels over every column
   but the scores, set apart from the student rows below it. It stays on
   top while the rows scroll under it, so it sits above everything in the
   body, including the sticky Name column and the score input boxes. */
/* Rows scrolling under the header must never show through it. The body is
   its own layer (isolation), so the sticky Group/Name cells, the Male /
   Female labels and the score boxes, whatever their z-index, stay inside
   it, below the header. The header row is solid too, so no gap between its
   cells shows the rows behind. */
.cr-table :deep(tbody) {
  position: relative;
  z-index: 0;
  isolation: isolate;
}
.cr-table :deep(thead) {
  position: relative;
  z-index: 3;
}
.cr-table :deep(thead tr) {
  background: color-mix(in srgb, var(--foreground) 8%, var(--card));
}
.cr-table :deep(thead th) {
  position: sticky !important;
  top: 0;
  z-index: 4 !important;
  height: 40px !important;
  /* A solid grey a step darker than the Male / Female rows (--accent);
     --subtle was so close to the white rows that the header looked
     see-through. Mixed from the theme, so dark mode gets its own grey. */
  background: color-mix(in srgb, var(--foreground) 8%, var(--card)) !important;
  color: var(--secondary-foreground) !important;
  font-size: 12px;
  font-weight: 600 !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid var(--border-strong) !important;
}
.cr-table :deep(thead th:nth-child(1)),
.cr-table :deep(thead th:nth-child(2)) {
  z-index: 5 !important;
}
.cr-table :deep(tbody td) {
  height: 44px !important;
}
.cr-table :deep(.cr-col-score) {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
/* A recorded score's header is tinted with the school colour, so the score
   columns stand apart from Group and Name. The tint sits on an opaque base
   because the header row stays on top while the rows scroll under it. */
.cr-table :deep(thead th.cr-col-score) {
  background: linear-gradient(var(--brand-soft), var(--brand-soft)), var(--card) !important;
  color: var(--brand) !important;
}
/* Score columns are boxed off from each other and from the columns on
   either side with a line on each side. Status and the grades are centred
   too, but without lines. */
.cr-table :deep(th.cr-col-score),
.cr-table :deep(td.cr-col-score) {
  border-left: 1px solid var(--border) !important;
  text-align: center !important;
}
.cr-table :deep(th.cr-col-score--last),
.cr-table :deep(td.cr-col-score--last) {
  border-right: 1px solid var(--border) !important;
}
.cr-table :deep(th.cr-col-grade),
.cr-table :deep(td.cr-col-grade) {
  text-align: center !important;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.cr-table :deep(tbody td.cr-col-score) {
  font-size: 14px;
  font-weight: 500;
}
.cr-table :deep(td.cr-col-score--editing) {
  background: var(--brand-soft);
}
.cr-table :deep(thead th.cr-col-score--editing) {
  background: var(--brand) !important;
  color: var(--brand-foreground, #fff) !important;
}
/* Male / Female rows: shaded, between the header and the students. The
   label stays in view while the score columns scroll. */
.cr-table :deep(tbody tr.cr-group td) {
  height: 36px !important;
  background: var(--accent);
  box-shadow: none;
}
.cr-group td {
  padding: 6px 12px !important;
  background: var(--subtle);
}
.cr-group__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  color: var(--foreground);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.cr-group__count {
  font-weight: 400;
  color: var(--muted-foreground);
}
.cr-score {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
}
.cr-score__input {
  width: 90px;
}
/* A score being edited in the class table: fits its column, centred like
   the scores around it. */
.cr-cell-input {
  width: 84px;
  margin: 0 auto;
}
.cr-cell-input :deep(input) {
  text-align: center;
}
.cr-score__max {
  font-size: 13px;
  color: var(--muted-foreground);
  white-space: nowrap;
}
.cr-reco {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 4px 0;
  white-space: normal;
}
.cr-advice {
  font-size: 12px;
  line-height: 1.4;
  color: var(--muted-foreground);
}
.cr-empty {
  padding: 32px 16px;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
.cr-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 24px;
  background: var(--card);
  border-top: 1px solid var(--border);
}
@media (max-width: 600px) {
  .cr-header,
  .cr-body,
  .cr-footer {
    padding-left: 16px;
    padding-right: 16px;
  }
  .cr-filters__field {
    flex: 1 1 100%;
  }
  .cr-entry__title-field,
  .cr-entry__total-field {
    flex: 1 1 100%;
  }
}

/* Add score dialog */
.cr-add {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.cr-add__fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px;
  gap: 12px;
}
.cr-add__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}
.cr-add__search {
  flex: 1 1 260px;
}
.cr-add__progress,
.cr-add__hint {
  margin: 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
.cr-add__progress strong {
  color: var(--foreground);
}
.cr-add__list {
  max-height: 52vh;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
}
.cr-add__group {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 6px 12px;
  background: var(--subtle);
  border-bottom: 1px solid var(--border);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.cr-add__group span {
  margin-left: 4px;
  color: var(--muted-foreground);
  font-weight: 500;
}
.cr-add__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--border);
}
.cr-add__row:last-child {
  border-bottom: 0;
}
.cr-add__row:focus-within {
  background: var(--accent);
}
.cr-add__who {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.cr-add__name {
  font-size: 14px;
  font-weight: 500;
}
.cr-add__lrn {
  font-size: 12px;
  color: var(--muted-foreground);
  font-variant-numeric: tabular-nums;
}
@media (max-width: 600px) {
  .cr-add__fields {
    grid-template-columns: minmax(0, 1fr);
  }
}
.cr-add__cols {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 6px 12px;
  background: color-mix(in srgb, var(--foreground) 8%, var(--card));
  border-bottom: 1px solid var(--border-strong);
  color: var(--secondary-foreground);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
/* Student | Needed to pass | Score, lined up from row to row */
.cr-add__list--advice .cr-add__cols,
.cr-add__list--advice .cr-add__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 190px 150px;
  align-items: center;
  gap: 12px;
}
.cr-add__list--advice .cr-add__group {
  top: 31px;
}
.cr-add__need {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  font-size: 14px;
}
@media (max-width: 600px) {
  .cr-add__list--advice .cr-add__cols {
    display: none;
  }
  .cr-add__list--advice .cr-add__row {
    grid-template-columns: minmax(0, 1fr) auto;
  }
  .cr-add__list--advice .cr-add__need {
    grid-row: 2;
  }
}
.cr-cell--empty {
  color: var(--faint-foreground);
}
.cr-table :deep(.cr-group__btn) {
  position: sticky;
  left: 12px;
}
.cr-cell--group {
  font-size: 13px;
  color: var(--muted-foreground);
}
/* Edit scores bar: title | search (grows) | Cancel, Save */
.cr-editbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  margin-bottom: 16px;
  padding: 10px 12px;
  border: 1px solid var(--border-strong);
  border-radius: 10px;
  background: linear-gradient(var(--brand-soft), var(--brand-soft)), var(--card);
}
.cr-editbar__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  white-space: nowrap;
}
.cr-editbar__title .v-icon {
  color: var(--brand);
}
.cr-editbar__max {
  color: var(--muted-foreground);
  font-size: 13px;
}
.cr-editbar__search {
  flex: 1 1 260px;
  min-width: 220px;
  max-width: 420px;
}
.cr-editbar__search :deep(.v-field) {
  background: var(--card);
}
.cr-editbar__actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}
.cr-toolbar__search {
  flex: 0 1 280px;
  min-width: 200px;
}
</style>
